import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { verifyCheckoutSession } from '@/services/stripeService';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasVerifiedRef = useRef(false); // Track if we've already verified

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Prevent multiple verifications
    if (hasVerifiedRef.current) {
      return;
    }

    const verifySubscription = async () => {
      // Wait for auth to finish loading
      if (isAuthLoading) {
        return;
      }

      // Check if session ID exists in URL
      if (!sessionId) {
        console.error('No session_id found in URL:', window.location.href);
        setError('Missing session ID. Please check the URL or contact support.');
        setIsLoading(false);
        return;
      }

      // Check if user is authenticated
      if (!user) {
        console.error('User not authenticated');
        // Redirect to login with return URL
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `/login?redirect=${returnUrl}`;
        return;
      }

      try {
        console.log('Verifying subscription for session:', sessionId, 'User:', user?.id);
        
        // First, try to verify the checkout session manually (in case webhook hasn't processed yet)
        // Wrap in try-catch to gracefully handle if function is not deployed yet
        try {
          await verifyCheckoutSession(sessionId);
          console.log('Checkout session verified successfully');
        } catch (verifyErr: any) {
          // Check if it's a CORS or network error (function not deployed)
          if (verifyErr.message?.includes('Failed to fetch') || verifyErr.message?.includes('CORS')) {
            console.warn('verifyCheckoutSession function may not be deployed yet. Webhook will handle subscription sync.');
          } else {
            console.warn('Manual verification failed (webhook may handle it):', verifyErr.message);
          }
          // Continue anyway - webhook might still process it
        }
        
        // Refresh subscription data (webhook should have already updated it)
        await refreshSubscription();
        
        // Wait a moment and refresh again to catch webhook updates
        setTimeout(async () => {
          await refreshSubscription();
          setIsLoading(false);
        }, 2000);
      } catch (err: any) {
        console.error('Error verifying subscription:', err);
        // Don't show error immediately - webhook might still be processing
        // Just wait and refresh again
        setTimeout(async () => {
          try {
            await refreshSubscription();
            setIsLoading(false);
          } catch (retryErr: any) {
            console.error('Retry failed:', retryErr);
            setError('Subscription verification failed. Your payment was processed, but there may be a delay in activation. Please check your subscription status.');
            setIsLoading(false);
          }
        }, 2000);
      } finally {
        hasVerifiedRef.current = true; // Mark as verified to prevent re-running
      }
    };

    verifySubscription();
  }, [sessionId, user?.id, isAuthLoading]); // Removed refreshSubscription from dependencies

  // Show loading while auth is loading or subscription is verifying
  if (isAuthLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isAuthLoading ? 'Loading...' : 'Verifying your subscription...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Subscription Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/subscription')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Subscription
            </button>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleNavigateToDashboard = async () => {
    // Ensure subscription is refreshed before navigating
    try {
      await refreshSubscription();
      // Set flag in sessionStorage so HomePage can detect we're coming from subscription success
      sessionStorage.setItem('fromSubscriptionSuccess', 'true');
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/home');
    } catch (error) {
      console.error('Error refreshing subscription before navigation:', error);
      // Set flag anyway - HomePage will refresh again
      sessionStorage.setItem('fromSubscriptionSuccess', 'true');
      navigate('/home');
    }
  };

  const handleNavigateToSettings = async () => {
    // Ensure subscription is refreshed before navigating
    try {
      await refreshSubscription();
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/settings');
    } catch (error) {
      console.error('Error refreshing subscription before navigation:', error);
      // Navigate anyway - subscription will be refreshed by context
      navigate('/settings');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Subscription Successful!</h1>
        <p className="text-gray-600 mb-6">
          Thank you for subscribing. Your subscription has been activated and you now have access to all features in your plan.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={handleNavigateToDashboard}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Dashboard
          </button>
          <button
            onClick={handleNavigateToSettings}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Manage Subscription
          </button>
        </div>
      </div>
    </div>
  );
}

